import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BionicText } from '../components/custom/BionicText';
import { DietCard } from '../components/custom/DietCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, 
  ArrowRight, 
  X,
  Loader2,
  Sparkles,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: 'Diet Types', description: 'Select your dietary preferences' },
  { id: 2, title: 'Allergens', description: 'Add any food allergies' },
  { id: 3, title: 'Plan Details', description: 'Configure your meal plan' },
];

const commonAllergens = [
  'Peanuts', 'Tree Nuts', 'Milk', 'Eggs', 'Wheat', 'Soy', 
  'Fish', 'Shellfish', 'Sesame', 'Mustard', 'Celery', 'Sulfites'
];

export default function DietBuilder() {
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [dietTypes, setDietTypes] = useState([]);
  const [selectedDiets, setSelectedDiets] = useState([]);
  const [allergens, setAllergens] = useState([]);
  const [customAllergen, setCustomAllergen] = useState('');
  const [planName, setPlanName] = useState('');
  const [servings, setServings] = useState(2);
  const [days, setDays] = useState(7);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    fetchDietTypes();
  }, []);

  const fetchDietTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/diet-types`);
      setDietTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch diet types:', error);
    }
  };

  const toggleDiet = (dietId) => {
    setSelectedDiets(prev => 
      prev.includes(dietId) 
        ? prev.filter(d => d !== dietId)
        : [...prev, dietId]
    );
  };

  const toggleAllergen = (allergen) => {
    setAllergens(prev =>
      prev.includes(allergen)
        ? prev.filter(a => a !== allergen)
        : [...prev, allergen]
    );
  };

  const addCustomAllergen = () => {
    if (customAllergen.trim() && !allergens.includes(customAllergen.trim())) {
      setAllergens([...allergens, customAllergen.trim()]);
      setCustomAllergen('');
    }
  };

  const handleNext = () => {
    if (step === 1 && selectedDiets.length === 0) {
      toast.error('Please select at least one diet type');
      return;
    }
    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    if (!planName.trim()) {
      toast.error('Please enter a name for your plan');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/diet-plans`,
        {
          name: planName,
          diet_types: selectedDiets,
          allergens,
          servings,
          days
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Diet plan created! Generating meals...');
      navigate(`/meal-plan/${response.data.plan_id}`);
    } catch (error) {
      toast.error('Failed to create diet plan');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const progress = (step / 3) * 100;

  return (
    <main className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 opacity-0 animate-fade-in">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-4 -ml-2"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
            Back to Dashboard
          </Button>
          
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
            <BionicText>Create Your Diet Plan</BionicText>
          </h1>
          <p className="text-muted-foreground mt-2">
            <BionicText>Build a personalized meal plan tailored to your dietary needs.</BionicText>
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8" role="progressbar" aria-valuenow={step} aria-valuemin={1} aria-valuemax={3}>
          <div className="flex justify-between mb-2">
            {STEPS.map((s) => (
              <div 
                key={s.id}
                className={`flex items-center gap-2 ${s.id === step ? 'text-primary' : s.id < step ? 'text-primary' : 'text-muted-foreground'}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  s.id === step ? 'bg-primary text-primary-foreground' : 
                  s.id < step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  {s.id < step ? <Check className="h-4 w-4" /> : s.id}
                </div>
                <span className="hidden sm:inline text-sm font-medium">{s.title}</span>
              </div>
            ))}
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step Content */}
        <Card className="opacity-0 animate-fade-in" data-testid="diet-builder-card">
          <CardHeader>
            <CardTitle>{STEPS[step - 1].title}</CardTitle>
            <CardDescription>{STEPS[step - 1].description}</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Step 1: Diet Types */}
            {step === 1 && (
              <div className="grid sm:grid-cols-2 gap-4">
                {dietTypes.map((diet) => (
                  <DietCard
                    key={diet.id}
                    diet={diet}
                    selected={selectedDiets.includes(diet.id)}
                    onSelect={toggleDiet}
                  />
                ))}
              </div>
            )}

            {/* Step 2: Allergens */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <Label className="text-base font-medium mb-3 block">Common Allergens</Label>
                  <div className="flex flex-wrap gap-2">
                    {commonAllergens.map((allergen) => (
                      <Badge
                        key={allergen}
                        variant={allergens.includes(allergen) ? "default" : "outline"}
                        className="cursor-pointer px-3 py-1.5 text-sm"
                        onClick={() => toggleAllergen(allergen)}
                        data-testid={`allergen-${allergen.toLowerCase()}`}
                      >
                        {allergens.includes(allergen) && (
                          <X className="h-3 w-3 mr-1" aria-hidden="true" />
                        )}
                        {allergen}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="custom-allergen">Add Custom Allergen</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="custom-allergen"
                      value={customAllergen}
                      onChange={(e) => setCustomAllergen(e.target.value)}
                      placeholder="e.g., Corn, Nightshades"
                      className="rounded-xl"
                      onKeyDown={(e) => e.key === 'Enter' && addCustomAllergen()}
                      data-testid="custom-allergen-input"
                    />
                    <Button 
                      type="button" 
                      onClick={addCustomAllergen}
                      className="rounded-xl"
                      data-testid="add-allergen-btn"
                    >
                      Add
                    </Button>
                  </div>
                </div>

                {allergens.length > 0 && (
                  <div>
                    <Label className="text-base font-medium mb-2 block">Selected Allergens</Label>
                    <div className="flex flex-wrap gap-2">
                      {allergens.map((allergen) => (
                        <Badge
                          key={allergen}
                          variant="secondary"
                          className="px-3 py-1.5"
                        >
                          {allergen}
                          <button
                            onClick={() => toggleAllergen(allergen)}
                            className="ml-2 hover:text-destructive"
                            aria-label={`Remove ${allergen}`}
                          >
                            <X className="h-3 w-3" aria-hidden="true" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-sm text-muted-foreground">
                  Skip this step if you don't have any food allergies.
                </p>
              </div>
            )}

            {/* Step 3: Plan Details */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <Label htmlFor="plan-name">Plan Name</Label>
                  <Input
                    id="plan-name"
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                    placeholder="e.g., My Weekly Keto Plan"
                    className="rounded-xl mt-2"
                    data-testid="plan-name-input"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="servings">Servings per Meal</Label>
                    <Input
                      id="servings"
                      type="number"
                      min={1}
                      max={10}
                      value={servings}
                      onChange={(e) => setServings(parseInt(e.target.value) || 1)}
                      className="rounded-xl mt-2"
                      data-testid="servings-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="days">Number of Days</Label>
                    <Input
                      id="days"
                      type="number"
                      min={1}
                      max={14}
                      value={days}
                      onChange={(e) => setDays(parseInt(e.target.value) || 1)}
                      className="rounded-xl mt-2"
                      data-testid="days-input"
                    />
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-muted/50 rounded-2xl p-4 space-y-3">
                  <h4 className="font-medium text-foreground">Plan Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Diet Types:</span>
                      <span className="font-medium">{selectedDiets.length} selected</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Allergens:</span>
                      <span className="font-medium">{allergens.length || 'None'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Duration:</span>
                      <span className="font-medium">{days} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Servings:</span>
                      <span className="font-medium">{servings} per meal</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={step === 1}
            className="rounded-full"
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
            Back
          </Button>

          {step < 3 ? (
            <Button 
              onClick={handleNext}
              className="rounded-full"
              data-testid="next-step-btn"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={loading || !planName.trim()}
              className="rounded-full gap-2"
              data-testid="create-plan-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Creating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                  Create & Generate Meals
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </main>
  );
}
